import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Users, FileText, Mail, Calendar,
  LogOut, Menu, X, GitBranch, Image as ImageIcon, LayoutGrid, Globe, CreditCard,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { CRMPipeline } from '../components/CRMPipeline';
import { API } from '../components/admin/constants';
import { OverviewTab } from '../components/admin/OverviewTab';
import { InquiriesTab } from '../components/admin/InquiriesTab';
import { ClientsTab } from '../components/admin/ClientsTab';
import { EventsTab } from '../components/admin/EventsTab';
import { BlogTab } from '../components/admin/BlogTab';
import { AssetsTab } from '../components/admin/AssetsTab';
import { PortfolioTab } from '../components/admin/PortfolioTab';
import { PaymentsTab } from '../components/admin/PaymentsTab';

const sidebarLinks = [
  { icon: LayoutDashboard, label: 'Overview', path: '/admin' },
  { icon: GitBranch, label: 'CRM Pipeline', path: '/admin/crm' },
  { icon: Mail, label: 'Inquiries', path: '/admin/inquiries' },
  { icon: Users, label: 'Portal Clients', path: '/admin/clients' },
  { icon: Calendar, label: 'Events', path: '/admin/events' },
  { icon: CreditCard, label: 'Payments', path: '/admin/payments' },
  { icon: FileText, label: 'Blog', path: '/admin/blog' },
  { icon: LayoutGrid, label: 'Portfolio', path: '/admin/portfolio' },
  { icon: ImageIcon, label: 'Brand Assets', path: '/admin/assets' },
];

export const Admin = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Data states
  const [inquiries, setInquiries] = useState([]);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hubspotStatus, setHubspotStatus] = useState(null);

  // Modal states
  const [showClientModal, setShowClientModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const currentPath = location.pathname;

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [inquiriesRes, clientsRes, eventsRes, blogRes] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/clients`, { withCredentials: true }),
        axios.get(`${API}/events`, { withCredentials: true }),
        axios.get(`${API}/blog?published_only=false`, { withCredentials: true }),
      ]);
      setInquiries(inquiriesRes.data);
      setClients(clientsRes.data);
      setEvents(eventsRes.data);
      setBlogPosts(blogRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchAllData();
    }
  }, [user, fetchAllData]);

  const fetchHubspotStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/integrations/hubspot/status`, { withCredentials: true });
      setHubspotStatus(res.data);
    } catch (err) {
      setHubspotStatus({
        connected: false,
        portal_id: null,
        pipeline_configured: false,
        stages_mapped: 0,
        stages_total: 7,
        message: 'Unable to reach HubSpot status endpoint.',
      });
    }
  }, []);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchHubspotStatus();
    }
  }, [user, fetchHubspotStatus]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#C6A86A] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') return null;

  const renderContent = () => {
    switch (currentPath) {
      case '/admin':
        return (
          <OverviewTab
            inquiries={inquiries}
            clients={clients}
            events={events}
            blogPosts={blogPosts}
            hubspotStatus={hubspotStatus}
            onRefreshHubspot={fetchHubspotStatus}
          />
        );
      case '/admin/crm':
        return <CRMPipeline hubspotStatus={hubspotStatus} />;
      case '/admin/inquiries':
        return <InquiriesTab inquiries={inquiries} setInquiries={setInquiries} hubspotStatus={hubspotStatus} />;
      case '/admin/clients':
        return (
          <ClientsTab
            clients={clients}
            setClients={setClients}
            showModal={showClientModal}
            setShowModal={setShowClientModal}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            fetchAllData={fetchAllData}
          />
        );
      case '/admin/events':
        return (
          <EventsTab
            events={events}
            clients={clients}
            showModal={showEventModal}
            setShowModal={setShowEventModal}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            fetchAllData={fetchAllData}
          />
        );
      case '/admin/blog':
        return (
          <BlogTab
            blogPosts={blogPosts}
            showModal={showBlogModal}
            setShowModal={setShowBlogModal}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            fetchAllData={fetchAllData}
          />
        );
      case '/admin/assets':
        return <AssetsTab />;
      case '/admin/portfolio':
        return <PortfolioTab />;
      case '/admin/payments':
        return <PaymentsTab />;
      default:
        return (
          <OverviewTab
            inquiries={inquiries}
            clients={clients}
            events={events}
            blogPosts={blogPosts}
            hubspotStatus={hubspotStatus}
            onRefreshHubspot={fetchHubspotStatus}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="admin-page">
      {/* Mobile Sidebar Toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-24 left-4 z-50 p-2 bg-[#0a0a0a] border border-white/10 text-white"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-20 left-0 h-[calc(100vh-5rem)] w-64 bg-[#0a0a0a] border-r border-white/10 z-40 transition-transform lg:translate-x-0 flex flex-col ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="p-6 pb-4">
            <p className="text-[#D4AF37] text-xs uppercase tracking-widest mb-2">Admin Dashboard</p>
            <p className="text-white font-medium truncate">{user?.name}</p>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 space-y-2">
            {sidebarLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => {
                  navigate(link.path);
                  setSidebarOpen(false);
                }}
                className={`flex items-center gap-3 px-4 py-2.5 transition-colors w-full text-left ${
                  currentPath === link.path
                    ? 'bg-[#D4AF37] text-white'
                    : 'text-white/60 hover:text-white hover:bg-[#050505]'
                }`}
                data-testid={`admin-nav-${link.label.toLowerCase()}`}
              >
                <link.icon size={18} />
                {link.label}
              </button>
            ))}
          </nav>

          <div className="p-6 pt-4 space-y-1 border-t border-white/5">
            <button
              onClick={() => {
                navigate('/');
                setSidebarOpen(false);
              }}
              className="flex items-center gap-3 text-white/60 hover:text-[#D4AF37] transition-colors w-full px-4 py-3 border border-white/10 hover:border-[#D4AF37]/50"
              data-testid="admin-back-to-website"
            >
              <Globe size={18} />
              Back to Website
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 text-white/60 hover:text-white transition-colors w-full px-4 py-3"
              data-testid="admin-logout"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-12 min-h-[calc(100vh-5rem)]">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-2 border-[#C6A86A] border-t-transparent rounded-full" />
            </div>
          ) : (
            renderContent()
          )}
        </main>
      </div>
    </div>
  );
};
